// AI Assistant - Comprehensive Suggested Prompts
// Specialized prompts for Magnus Resolve reliability engineering
// 6 Categories with 42+ total prompts for FRACAS, 8D, Lab, RCA, Analytics, and Cross-Module
// ============================================================

import React from 'react';
import {
    Activity,
    AlertTriangle,
    BarChart3,
    Beaker,
    Brain,
    CheckCircle,
    ClipboardList,
    DollarSign,
    FileText,
    GitBranch,
    Link as LinkIcon,
    Search,
    Shield,
    Target,
    TestTube2,
    TrendingUp,
    Users,
    Workflow,
    Wrench,
    LineChart,
    Package,
    MapPin,
    Calendar,
} from 'lucide-react';

export interface AISuggestedPrompt {
    id: string;
    category: AICategoryType;
    icon: React.ReactNode;
    title: string;
    prompt: string;
    description: string;
}

export type AICategoryType =
    | 'FRACAS Intelligence'
    | '8D Problem Solving'
    | 'Lab Integration'
    | 'Reliability Analytics'
    | 'RCA Methodology'
    | 'Cross-Module Intelligence';

export const AI_CATEGORIES = [
    {
        id: 'FRACAS Intelligence' as AICategoryType,
        icon: React.createElement(AlertTriangle, { className: "text-red-500" }),
        color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
        activeColor: 'bg-red-500 text-white',
        description: 'Incident analysis and failure patterns'
    },
    {
        id: '8D Problem Solving' as AICategoryType,
        icon: React.createElement(Users, { className: "text-blue-500" }),
        color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
        activeColor: 'bg-blue-500 text-white',
        description: '8 Disciplines structured problem solving'
    },
    {
        id: 'Lab Integration' as AICategoryType,
        icon: React.createElement(Beaker, { className: "text-purple-500" }),
        color: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
        activeColor: 'bg-purple-500 text-white',
        description: 'Test planning and result interpretation'
    },
    {
        id: 'Reliability Analytics' as AICategoryType,
        icon: React.createElement(BarChart3, { className: "text-green-500" }),
        color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
        activeColor: 'bg-green-500 text-white',
        description: 'Weibull, MTBF, warranty forecasting'
    },
    {
        id: 'RCA Methodology' as AICategoryType,
        icon: React.createElement(GitBranch, { className: "text-amber-500" }),
        color: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100',
        activeColor: 'bg-amber-500 text-white',
        description: '5-Why, Ishikawa, FTA analysis'
    },
    {
        id: 'Cross-Module Intelligence' as AICategoryType,
        icon: React.createElement(LinkIcon, { className: "text-indigo-500" }),
        color: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100',
        activeColor: 'bg-indigo-500 text-white',
        description: 'FRACAS → Lab → 8D → PFMEA/DFMEA linkage'
    },
];

export const getAISuggestedPrompts = (): AISuggestedPrompt[] => {
    // ========== FRACAS INTELLIGENCE (7 prompts) ==========
    const fracasPrompts: AISuggestedPrompt[] = [
        {
            id: 'fracas-1',
            category: 'FRACAS Intelligence',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Top Failure Analysis',
            prompt: 'Analyze my top 5 FRACAS incidents by severity and cost impact. Show me patterns in product, component, and failure mode.',
            description: 'Identify highest-impact failures'
        },
        {
            id: 'fracas-2',
            category: 'FRACAS Intelligence',
            icon: React.createElement(BarChart3, { size: 16 }),
            title: 'Failure Trend Analysis',
            prompt: 'Show me failure trends over the last 6 months. Which failure modes are increasing? Which products have improving reliability?',
            description: 'Track reliability trends'
        },
        {
            id: 'fracas-3',
            category: 'FRACAS Intelligence',
            icon: React.createElement(Search, { size: 16 }),
            title: 'Similar Incident Finder',
            prompt: 'Find similar incidents to FRAC-001. Look for matching products, failure modes, or root causes.',
            description: 'Detect recurring issues'
        },
        {
            id: 'fracas-4',
            category: 'FRACAS Intelligence',
            icon: React.createElement(DollarSign, { size: 16 }),
            title: 'Cost Impact Analysis',
            prompt: 'Calculate total cost of quality from FRACAS incidents. Break down by product line, failure mode, and severity.',
            description: 'Financial impact assessment'
        },
        {
            id: 'fracas-5',
            category: 'FRACAS Intelligence',
            icon: React.createElement(MapPin, { size: 16 }),
            title: 'Geographic Clustering',
            prompt: 'Analyze failures by location. Are there environmental or regional patterns? Which locations have highest failure rates?',
            description: 'Location-based analysis'
        },
        {
            id: 'fracas-6',
            category: 'FRACAS Intelligence',
            icon: React.createElement(AlertTriangle, { size: 16 }),
            title: 'Escalation Recommendations',
            prompt: 'Which open incidents should be escalated to 8D? Recommend based on severity, quantity affected, and customer impact.',
            description: 'Prioritize critical issues'
        },
        {
            id: 'fracas-7',
            category: 'FRACAS Intelligence',
            icon: React.createElement(Calendar, { size: 16 }),
            title: 'Closure Prediction',
            prompt: 'Predict time to close for open incidents. Which ones are at risk of overdue? What actions are needed to accelerate closure?',
            description: 'Timeline forecasting'
        },
    ];

    // ========== 8D PROBLEM SOLVING (7 prompts) ==========
    const eightDPrompts: AISuggestedPrompt[] = [
        {
            id: '8d-1',
            category: '8D Problem Solving',
            icon: React.createElement(Users, { size: 16 }),
            title: 'Team Formation Guide',
            prompt: 'Help me form an 8D team for this problem. What roles and expertise are needed? Who should be the team leader?',
            description: 'D0: Build the team'
        },
        {
            id: '8d-2',
            category: '8D Problem Solving',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'Problem Statement',
            prompt: 'Review my problem description. Is it specific enough? Does it include who, what, when, where, and how many?',
            description: 'D1: Define the problem'
        },
        {
            id: '8d-3',
            category: '8D Problem Solving',
            icon: React.createElement(Shield, { size: 16 }),
            title: 'Containment Actions',
            prompt: 'Suggest containment actions for this failure mode. What immediate steps prevent the problem from reaching customers?',
            description: 'D2: Contain the issue'
        },
        {
            id: '8d-4',
            category: '8D Problem Solving',
            icon: React.createElement(GitBranch, { size: 16 }),
            title: 'Root Cause Validation',
            prompt: 'Validate my root cause analysis. Is it deep enough? Does it explain all symptoms? Use 5-Why checking.',
            description: 'D3: Find true root cause'
        },
        {
            id: '8d-5',
            category: '8D Problem Solving',
            icon: React.createElement(Wrench, { size: 16 }),
            title: 'Corrective Action Plan',
            prompt: 'Recommend corrective actions for this root cause. What changes to design, process, or controls will eliminate the problem?',
            description: 'D4: Plan permanent fix'
        },
        {
            id: '8d-6',
            category: '8D Problem Solving',
            icon: React.createElement(CheckCircle, { size: 16 }),
            title: 'Action Effectiveness',
            prompt: 'How can I validate that my corrective action worked? What data should I collect? How long should I monitor?',
            description: 'D5: Verify effectiveness'
        },
        {
            id: '8d-7',
            category: '8D Problem Solving',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Preventive Actions',
            prompt: 'Suggest preventive actions to avoid similar problems in other products or processes. Where else could this occur?',
            description: 'D6: Prevent recurrence'
        },
    ];

    // ========== LAB INTEGRATION (7 prompts) ==========
    const labPrompts: AISuggestedPrompt[] = [
        {
            id: 'lab-1',
            category: 'Lab Integration',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Test Prioritization',
            prompt: 'Which lab samples should I prioritize for testing? Rank by customer impact, severity, and business urgency.',
            description: 'Optimize test schedule'
        },
        {
            id: 'lab-2',
            category: 'Lab Integration',
            icon: React.createElement(TestTube2, { size: 16 }),
            title: 'Test Plan Recommendations',
            prompt: 'What tests should I request for this failure mode? Suggest ASTM standards, equipment needs, and expected results.',
            description: 'Define test strategy'
        },
        {
            id: 'lab-3',
            category: 'Lab Integration',
            icon: React.createElement(Activity, { size: 16 }),
            title: 'Result Interpretation',
            prompt: 'Interpret test results for LAB-002. What do the measurements mean? Do they confirm the suspected failure mode?',
            description: 'Understand test data'
        },
        {
            id: 'lab-4',
            category: 'Lab Integration',
            icon: React.createElement(Search, { size: 16 }),
            title: 'Failure Mode Confirmation',
            prompt: 'Do lab results confirm the FRACAS failure mode? If not, what alternative failure modes fit the test data?',
            description: 'Validate or refute hypothesis'
        },
        {
            id: 'lab-5',
            category: 'Lab Integration',
            icon: React.createElement(Wrench, { size: 16 }),
            title: 'Material Analysis Guide',
            prompt: 'Guide me through material failure analysis. What tests reveal hardness, composition, microstructure, and degradation?',
            description: 'Metallurgical investigation'
        },
        {
            id: 'lab-6',
            category: 'Lab Integration',
            icon: React.createElement(ClipboardList, { size: 16 }),
            title: 'Testing Standards Lookup',
            prompt: 'What ASTM/ISO standards apply to testing this component? Show me test methods, acceptance criteria, and procedures.',
            description: 'Find relevant standards'
        },
        {
            id: 'lab-7',
            category: 'Lab Integration',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'Lab Report Generation',
            prompt: 'Generate a lab test report summary for this sample. Include findings, conclusions, and recommendations.',
            description: 'Create test documentation'
        },
    ];

    // ========== RELIABILITY ANALYTICS (7 prompts) ==========
    const analyticsPrompts: AISuggestedPrompt[] = [
        {
            id: 'analytics-1',
            category: 'Reliability Analytics',
            icon: React.createElement(LineChart, { size: 16 }),
            title: 'Weibull Parameter Interpretation',
            prompt: 'Explain my Weibull results: Beta=2.3, Eta=4500 hours. What does this mean for reliability? When should I schedule PM?',
            description: 'Understand Weibull analysis'
        },
        {
            id: 'analytics-2',
            category: 'Reliability Analytics',
            icon: React.createElement(Target, { size: 16 }),
            title: 'MTBF Optimization',
            prompt: 'How can I improve MTBF from 1200 to 2000 hours? Analyze failure modes and suggest design or maintenance changes.',
            description: 'Increase reliability'
        },
        {
            id: 'analytics-3',
            category: 'Reliability Analytics',
            icon: React.createElement(DollarSign, { size: 16 }),
            title: 'Warranty Cost Drivers',
            prompt: 'What is driving warranty costs? Break down by product, failure mode, and region. Where should I focus improvement efforts?',
            description: 'Reduce warranty expense'
        },
        {
            id: 'analytics-4',
            category: 'Reliability Analytics',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Failure Rate Trending',
            prompt: 'Show me failure rate trends (λ) over product lifecycle. Are we in infant mortality, useful life, or wear-out phase?',
            description: 'Lifecycle analysis'
        },
        {
            id: 'analytics-5',
            category: 'Reliability Analytics',
            icon: React.createElement(Calendar, { size: 16 }),
            title: 'B10 Life Scheduling',
            prompt: 'Based on B10=3500 hours, recommend preventive maintenance schedule. At what interval should I replace components?',
            description: 'PM planning'
        },
        {
            id: 'analytics-6',
            category: 'Reliability Analytics',
            icon: React.createElement(Activity, { size: 16 }),
            title: 'Reliability Growth',
            prompt: 'Track reliability growth from design changes. Has MTBF improved after implementing corrective actions?',
            description: 'Measure improvement'
        },
        {
            id: 'analytics-7',
            category: 'Reliability Analytics',
            icon: React.createElement(Package, { size: 16 }),
            title: 'Component Comparison',
            prompt: 'Compare reliability of Component A vs Component B. Which has lower failure rate? Better MTBF? Lower warranty cost?',
            description: 'Benchmarking analysis'
        },
    ];

    // ========== RCA METHODOLOGY (7 prompts) ==========
    const rcaPrompts: AISuggestedPrompt[] = [
        {
            id: 'rca-1',
            category: 'RCA Methodology',
            icon: React.createElement(GitBranch, { size: 16 }),
            title: '5-Why Drill-Down',
            prompt: 'Guide me through 5-Why analysis for this failure. Are my "whys" deep enough? Am I finding root cause or stopping at symptoms?',
            description: 'Deep cause finding'
        },
        {
            id: 'rca-2',
            category: 'RCA Methodology',
            icon: React.createElement(Workflow, { size: 16 }),
            title: 'Ishikawa Categories',
            prompt: 'Help me build an Ishikawa diagram. Suggest causes in each category: Man, Machine, Material, Method, Measurement, Environment.',
            description: 'Fishbone brainstorming'
        },
        {
            id: 'rca-3',
            category: 'RCA Methodology',
            icon: React.createElement(GitBranch, { size: 16 }),
            title: 'FTA Tree Building',
            prompt: 'Guide me in building a fault tree for this top event. What AND/OR gates should I use? What are the basic events?',
            description: 'Fault logic structure'
        },
        {
            id: 'rca-4',
            category: 'RCA Methodology',
            icon: React.createElement(BarChart3, { size: 16 }),
            title: 'Probability Calculation',
            prompt: 'Calculate top event probability from my fault tree. Show me which basic events contribute most to system failure.',
            description: 'FTA quantification'
        },
        {
            id: 'rca-5',
            category: 'RCA Methodology',
            icon: React.createElement(ClipboardList, { size: 16 }),
            title: 'Evidence Requirements',
            prompt: 'What evidence do I need to prove this root cause? What data, tests, or observations confirm the hypothesis?',
            description: 'Validate with data'
        },
        {
            id: 'rca-6',
            category: 'RCA Methodology',
            icon: React.createElement(CheckCircle, { size: 16 }),
            title: 'Root Cause Validation',
            prompt: 'Validate my root cause: Does it explain all symptoms? If removed, does problem disappear? Is it controllable?',
            description: 'Verify true root cause'
        },
        {
            id: 'rca-7',
            category: 'RCA Methodology',
            icon: React.createElement(Shield, { size: 16 }),
            title: 'Control Definition',
            prompt: 'Define controls to prevent this root cause. What process changes, error-proofing, or inspections eliminate the cause?',
            description: 'Prevent recurrence'
        },
    ];

    // ========== CROSS-MODULE INTELLIGENCE (7 prompts) ==========
    const crossModulePrompts: AISuggestedPrompt[] = [
        {
            id: 'cross-1',
            category: 'Cross-Module Intelligence',
            icon: React.createElement(LinkIcon, { size: 16 }),
            title: 'FRACAS to PFMEA Linkage',
            prompt: 'Trace FRAC-001 to PFMEA. Was this failure mode predicted? Do PFMEA controls need updating based on field data?',
            description: 'Field → Process feedback'
        },
        {
            id: 'cross-2',
            category: 'Cross-Module Intelligence',
            icon: React.createElement(Beaker, { size: 16 }),
            title: 'Lab to Design Feedback',
            prompt: 'Which lab results should feed into DFMEA? What design changes do test findings recommend?',
            description: 'Test → Design improvement'
        },
        {
            id: 'cross-3',
            category: 'Cross-Module Intelligence',
            icon: React.createElement(DollarSign, { size: 16 }),
            title: 'Warranty to Process',
            prompt: 'Link warranty claims to PFMEA process steps. Which manufacturing operations cause expensive field failures?',
            description: 'Cost → Process focus'
        },
        {
            id: 'cross-4',
            category: 'Cross-Module Intelligence',
            icon: React.createElement(Users, { size: 16 }),
            title: '8D Action Validation',
            prompt: 'Which 8D corrective actions have lab test validation? Show me actions with confirmed effectiveness.',
            description: 'Problem solving → Lab proof'
        },
        {
            id: 'cross-5',
            category: 'Cross-Module Intelligence',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Field to Process Changes',
            prompt: 'Show me FRACAS incidents that led to PFMEA control improvements. Has field data strengthened process controls?',
            description: 'Failure → Prevention'
        },
        {
            id: 'cross-6',
            category: 'Cross-Module Intelligence',
            icon: React.createElement(Target, { size: 16 }),
            title: 'End-to-End Traceability',
            prompt: 'Trace the complete failure journey: Field failure → Lab test → Root cause → 8D action → Design/process fix → Validation.',
            description: 'Full problem lifecycle'
        },
        {
            id: 'cross-7',
            category: 'Cross-Module Intelligence',
            icon: React.createElement(Brain, { size: 16 }),
            title: 'Predictive Intelligence',
            prompt: 'Based on all data (FRACAS, Lab, 8D, PFMEA/DFMEA), predict next month\'s critical failures and recommend preventive actions.',
            description: 'AI-powered forecasting'
        },
    ];

    return [
        ...fracasPrompts,
        ...eightDPrompts,
        ...labPrompts,
        ...analyticsPrompts,
        ...rcaPrompts,
        ...crossModulePrompts,
    ];
};
